import styled from 'styled-components';

export const brandPurple = '#502896';
export const brandMauve = '#854ee0';

export const PageShell = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f3eefb 0%, #e8e0f4 32%, #f5f2fa 100%);
  padding: 24px 16px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

export const TopBar = styled.header`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

export const BrandLogo = styled.img`
  height: 40px;
  max-width: 220px;
  object-fit: contain;
`;

export const Subtitle = styled.p`
  margin: 10px 0 0;
  font-size: 15px;
  line-height: 1.4;
  color: #6b6f77;
  text-align: center;
  max-width: 360px;
`;

export const MainCard = styled.div`
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(80, 40, 150, 0.12);
  border: 1px solid rgba(80, 40, 150, 0.08);
  padding: 24px 20px 28px;
  box-sizing: border-box;
`;

export const CardSection = styled.div`
  & + & {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #ebe6f2;
  }
`;

export const TitleRow = styled.h1`
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  color: #393939;
  text-align: center;
  line-height: 1.3;
`;

export const AmountBlock = styled.div`
  text-align: center;
  margin: 4px 0 16px;
`;

export const AmountLabel = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #6b6f77;
  letter-spacing: 0.02em;
  margin-bottom: 4px;
`;

export const AmountValue = styled.span`
  font-size: 32px;
  font-weight: 700;
  color: ${brandPurple};
  letter-spacing: -0.02em;
`;

export const DescriptionText = styled.p`
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.5;
  color: #393939;
  word-break: break-word;
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 12px;
  font-size: 14px;
  color: #6b6f77;
`;

export const PayerName = styled.span`
  color: #393939;
  font-weight: 500;
`;

export const Badge = styled.span<{ $variant: 'default' | 'success' | 'warning' | 'danger' }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return `
          background: #e8f7ee;
          color: #1e7a4a;
        `;
      case 'warning':
        return `
          background: #fff4e0;
          color: #a66b00;
        `;
      case 'danger':
        return `
          background: #fce8e8;
          color: #b82c2c;
        `;
      default:
        return `
          background: #ede6f7;
          color: ${brandPurple};
        `;
    }
  }}
`;

export const SectionTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: ${brandMauve};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CenteredLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: #6b6f77;
  font-size: 15px;
  gap: 12px;
`;

export const ErrorBox = styled.div`
  width: 100%;
  max-width: 480px;
  padding: 16px 18px;
  background: #fce8e8;
  border: 1px solid #e8b4b4;
  border-radius: 12px;
  color: #7a1f1f;
  font-size: 15px;
  line-height: 1.45;
  box-sizing: border-box;
`;

export const TextMono = styled.div`
  background: #f5f2fa;
  border: 1px solid #e2daef;
  border-radius: 10px;
  padding: 14px 12px;
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
  color: #2d2640;
  max-height: 200px;
  overflow-y: auto;
`;

export const ActionButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 14px 18px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ theme }) => theme.main?.color || '#59249B'};
  color: #fff;
  transition: filter 0.15s ease, transform 0.1s ease;

  &:hover {
    filter: brightness(1.06);
  }

  &:active {
    transform: scale(0.99);
  }

  &:focus {
    outline: 2px solid ${brandMauve};
    outline-offset: 2px;
  }
`;

export const SecondaryButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 10px;
  padding: 12px 16px;
  border: 1px solid #d0c4e4;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  background: #fff;
  color: ${brandPurple};
  transition: background 0.15s ease;

  &:hover {
    background: #faf8fc;
  }
`;

export const QrWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0 16px;
`;

export const QrImage = styled.img`
  width: 220px;
  height: 220px;
  border-radius: 12px;
  border: 1px solid #e2daef;
  background: #fff;
`;

export const CountdownPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: #ede6f7;
  border-radius: 10px;
  font-size: 14px;
  color: ${brandPurple};
  font-weight: 500;
`;

export const HelpText = styled.p`
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.45;
  color: #6b6f77;
`;

export const StatusPanel = styled.div<{ $kind: 'success' | 'warning' | 'danger' }>`
  display: flex;
  gap: 12px;
  padding: 18px 16px;
  border-radius: 12px;
  ${({ $kind }) => {
    if ($kind === 'success') {
      return `
        background: #e8f7ee;
        border: 1px solid #a8d9bc;
        color: #1a5c38;
      `;
    }
    if ($kind === 'warning') {
      return `
        background: #fff8e6;
        border: 1px solid #e8d48a;
        color: #6b4f00;
      `;
    }
    return `
      background: #fff0f0;
      border: 1px solid #e8b4b4;
      color: #7a1f1f;
    `;
  }}
`;

export const StatusIcon = styled.div`
  flex-shrink: 0;
  font-size: 24px;
  line-height: 1;
`;

export const StatusBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StatusHeading = styled.strong`
  display: block;
  font-size: 17px;
  margin-bottom: 6px;
`;

export const StatusMessage = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  opacity: 0.95;
`;
